import {
  LocalStorageKey,
  StoredLastAuthMethod,
  writeLocalStorage,
} from "@/lib/local-storage";
import type { AMREntry, SupabaseClient } from "@supabase/supabase-js";

// Second factors (totp, recovery) are excluded so an MFA verification does not
// mask the first-factor method that actually signed the user in.
// "oauth" is resolved to a concrete provider via the user's identities, since
// the AMR claim does not say which OAuth provider was used.
const FIRST_FACTOR_METHOD_MAP: Record<string, StoredLastAuthMethod | "oauth"> =
  {
    password: "email",
    otp: "email",
    magiclink: "email",
    oauth: "oauth",
    "sso/saml": "saml_sso",
  };

const OAUTH_PROVIDER_METHODS: Record<string, StoredLastAuthMethod> = {
  google: "google",
  azure: "microsoft",
};

async function resolveOAuthMethod(
  supabase: SupabaseClient
): Promise<StoredLastAuthMethod | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const latestOauthIdentity = (session?.user.identities ?? [])
    .filter((identity) => identity.provider in OAUTH_PROVIDER_METHODS)
    .sort(
      (a, b) =>
        Date.parse(b.last_sign_in_at ?? "") -
        Date.parse(a.last_sign_in_at ?? "")
    )[0];
  if (!latestOauthIdentity) {
    return null;
  }
  return OAUTH_PROVIDER_METHODS[latestOauthIdentity.provider];
}

/**
 * Records which method produced the current session, read from the session's
 * AMR claims. Runs after auth completes (not on button click), so abandoned
 * OAuth/SSO redirects never move the "Last used" badge.
 */
export async function recordLastAuthMethodFromSession(
  supabase: SupabaseClient
) {
  const { data, error } =
    await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) {
    return;
  }
  // The API may return bare method strings instead of full AMR entries.
  const entries: AMREntry[] = data.currentAuthenticationMethods.map((entry) =>
    typeof entry === "string" ? { method: entry, timestamp: 0 } : entry
  );
  const latestFirstFactor = entries
    .filter((entry) => entry.method in FIRST_FACTOR_METHOD_MAP)
    .sort((a, b) => b.timestamp - a.timestamp)[0];
  if (!latestFirstFactor) {
    return;
  }
  const mapped = FIRST_FACTOR_METHOD_MAP[latestFirstFactor.method];
  const method =
    mapped === "oauth" ? await resolveOAuthMethod(supabase) : mapped;
  if (!method) {
    return;
  }
  writeLocalStorage(LocalStorageKey.LastAuthMethod, method);
}
